import type { InvestmentProposalResponse, ProposalListResult } from "@/domain/investmentProposal";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject, listSlot, type PageShape } from "../cache/cacheSlots";
import { defineEntityCache } from "../cache/entityCache";

export type ProposalPatch = Partial<InvestmentProposalResponse>;

const page: PageShape<ProposalListResult, InvestmentProposalResponse, string> = {
  isPage: (value): value is ProposalListResult =>
    isObject(value) && Array.isArray(value.proposals) && typeof value.total_count === "number",
  records: (current) => current.proposals,
  withRecords: (current, proposals, countDelta) => ({
    ...current,
    proposals,
    total_count: current.total_count + countDelta,
  }),
  idOf: (record) => record.uuid,
};

function isProposal(value: unknown): value is InvestmentProposalResponse {
  return isObject(value) && typeof value.uuid === "string" && isObject(value.investor);
}

const merge = (
  record: InvestmentProposalResponse,
  patch: ProposalPatch,
): InvestmentProposalResponse => ({ ...record, ...patch });

/** The organization's proposals plus the one its investor opens from the public link. */
export const proposalCache = defineEntityCache<string, ProposalPatch>({
  entity: InvalidationEntity.PROPOSAL,
  slots: (orgId) => [
    listSlot({ prefix: queryKeys.proposals.lists(orgId), shape: page, merge }),
    detailSlot({
      prefix: queryKeys.proposals.details(orgId),
      isRecord: isProposal,
      idOf: page.idOf,
      merge,
    }),
    detailSlot({
      prefix: queryKeys.proposals.publicAll(),
      isRecord: isProposal,
      idOf: page.idOf,
      merge,
    }),
  ],
  records: (orgId, uuid) => [
    queryKeys.proposals.detail(orgId, uuid),
    queryKeys.proposals.publicOf(uuid),
  ],
});
